import React, { useState, useEffect } from 'react';
import {
  Typography,
  Row,
  Col,
  Card,
  Tag,
  Divider,
  Space,
  Spin,
  Breadcrumb,
  Timeline,
  Image,
  message,
  Descriptions,
  Progress,
  Rate,
  Alert,
  Button,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CarOutlined,
  InboxOutlined,
  FileSearchOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import RateHistoryButton from '../components/RateHistoryButton';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const GOLD = '#b5892a';

const ORDER_STATUS_CONFIG_KEYS = {
  pending_payment: { labelKey: 'orders.statusPending', color: 'orange' },
  paid: { labelKey: 'orders.statusPaid', color: 'blue' },
  inspecting: { labelKey: 'orders.statusShipping', color: 'cyan' },
  inspection_done: { labelKey: 'orders.statusShipping', color: 'cyan' },
  ready_to_ship: { labelKey: 'orders.statusShipping', color: 'cyan' },
  shipping: { labelKey: 'orders.statusShipping', color: 'geekblue' },
  delivered: { labelKey: 'orders.statusDelivered', color: 'success' },
  completed: { labelKey: 'orders.statusCompleted', color: 'success' },
  cancelled: { labelKey: 'orders.statusCancelled', color: 'default' },
  refunded: { labelKey: 'orders.statusRefunded', color: 'default' },
};

const GRADE_COLORS = {
  S: '#52c41a',
  A: '#1677ff',
  B: '#faad14',
  C: '#ff4d4f',
};

function formatCNY(val) {
  if (val == null) return '—';
  return '¥' + Number(val).toFixed(2);
}

function formatJPY(val) {
  if (val == null) return '—';
  return '¥' + Number(val).toLocaleString('ja-JP');
}

export default function OrderDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/orders/${id}`)
      .then(({ data }) => setOrder(data))
      .catch(() => message.error(t('common.failed')))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '80px 0' }}><Spin size="large" /></div>;
  }

  if (!order) {
    return <div style={{ padding: '40px', textAlign: 'center' }}><Text type="secondary">{t('common.noData')}</Text></div>;
  }

  const statusCfgRaw = ORDER_STATUS_CONFIG_KEYS[order.status] || { labelKey: null, color: 'default' };
  const statusCfg = { label: statusCfgRaw.labelKey ? t(statusCfgRaw.labelKey) : order.status, color: statusCfgRaw.color };

  // 后端返回 image_urls / title_zh / title_ja（join 自 auction_lots 表）
  let lotImages = [];
  try {
    const raw = order.image_urls || order.lot_image_urls;
    lotImages = typeof raw === 'string' ? JSON.parse(raw) : (raw || []);
  } catch (_) {}
  const lotTitle = order.title_zh || order.title_ja || order.lot_title || t('browse.title');

  // 检品报告：只有 admin 录入了 overall_grade 才视为已完成 → 才显示入口
  // 后端 /api/orders/:id 返回的是扁平字段（inspection_grade / overall_grade / appearance_score 等），
  // 这里重组成 inspection 对象供下方组件使用
  const inspection = order.overall_grade ? {
    id      : order.inspection_id,
    grade   : order.overall_grade,
    status  : order.inspection_status,
    scores  : {
      [t('orders.scoreAppearance')]: order.appearance_score,
      [t('orders.scoreFunction')]: order.function_score,
      [t('orders.scoreAccessory')]: order.accessory_score,
      [t('orders.scoreAuthenticity')]: order.authenticity_score,
      [t('orders.scorePackaging')]: order.packaging_score,
    },
    notes      : order.inspection_notes,
    photos     : order.inspection_photos,
    completed_at: order.completed_at,
  } : null;
  const shipment = order.shipment || null;

  // Build shipment timeline
  const shipEvents = [];
  if (shipment) {
    if (shipment.shipped_at) {
      shipEvents.push({ time: shipment.shipped_at, label: t('orders.shipped'), icon: <CarOutlined /> });
    }
    if (shipment.customs_cleared_at) {
      shipEvents.push({ time: shipment.customs_cleared_at, label: t('orders.customsCleared'), icon: <CheckCircleOutlined /> });
    }
    if (shipment.delivered_at) {
      shipEvents.push({ time: shipment.delivered_at, label: t('orders.delivered2'), icon: <InboxOutlined /> });
    }
    if (shipment.events && Array.isArray(shipment.events)) {
      shipment.events.forEach((ev) => {
        shipEvents.push({ time: ev.time || ev.created_at, label: ev.description || ev.label });
      });
    }
    shipEvents.sort((a, b) => dayjs(b.time).valueOf() - dayjs(a.time).valueOf());
  }

  let inspectionPhotos = [];
  if (inspection?.photos) {
    try {
      inspectionPhotos = typeof inspection.photos === 'string'
        ? JSON.parse(inspection.photos)
        : inspection.photos;
    } catch (_) {}
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1000, margin: '0 auto' }}>
      <Breadcrumb
        style={{ marginBottom: 20 }}
        items={[
          { title: <Link to="/orders">{t('orders.title')}</Link> },
          { title: `${t('orders.orderNo')} ${order.order_no}` },
        ]}
      />

      {/* 待付款提示：仅在扣款失败且未付款时显示 */}
      {order.deduct_error && !order.deducted_at && (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 20, borderRadius: 8 }}
          message={
            <Text strong style={{ fontSize: 14 }}>
              {t('orders.unpaidNotice')} · {t('orders.unpaidAmount')} {Number(order.total_amount_cny || 0).toFixed(2)} CNY
            </Text>
          }
          description={
            <div style={{ marginTop: 6 }}>
              <Text style={{ fontSize: 12, color: '#666' }}>
                {t('orders.unpaidReason')}：{order.deduct_error || t('orders.unpaidFallback')}
              </Text>
              <div style={{ marginTop: 10 }}>
                <Button
                  type="primary"
                  danger
                  onClick={() => navigate('/bills', { state: { focusOrderId: order.id } })}
                >
                  {t('orders.goToBill')}
                </Button>
              </div>
            </div>
          }
        />
      )}

      <Row gutter={[24, 24]}>
        {/* Order info */}
        <Col xs={24} md={14}>
          <Card
            title={
              <Space>
                <Text strong>{t('orders.orderInfo')}</Text>
                <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
              </Space>
            }
            style={{ borderRadius: 8, marginBottom: 0 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('orders.orderNo')}>{order.order_no}</Descriptions.Item>
              <Descriptions.Item label={t('orders.createTime')}>
                {order.created_at ? dayjs(order.created_at).format('YYYY-MM-DD HH:mm') : '—'}
              </Descriptions.Item>
              {order.paid_at && (
                <Descriptions.Item label={t('orders.payTime')}>
                  {dayjs(order.paid_at).format('YYYY-MM-DD HH:mm')}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider style={{ margin: '16px 0' }} />

            {/* Lot info */}
            <Space align="start" style={{ width: '100%' }}>
              {lotImages.length > 0 && (
                <img
                  src={lotImages[0]}
                  alt={lotTitle}
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 6,
                    flexShrink: 0,
                  }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text
                  strong
                  style={{ display: 'block', marginBottom: 4 }}
                >
                  {lotTitle}
                </Text>
                {order.house_name && (
                  <Text style={{ color: '#888', fontSize: 13 }}>{order.house_name}</Text>
                )}
                {order.final_price_jpy && (
                  <Text style={{ display: 'block', color: '#888', fontSize: 13 }}>
                    {t('orders.dealPrice')}：{formatJPY(order.final_price_jpy)}
                  </Text>
                )}
              </div>
            </Space>

            <Divider style={{ margin: '16px 0' }} />

            {/* Price breakdown */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 12 }}>{t('orders.priceBreakdown')}</Text>
              {order.exchange_rate && (
                <Row justify="space-between" align="middle" style={{ marginBottom: 6 }}>
                  <Text style={{ color: '#888' }}>{t('orders.exchangeRate')}</Text>
                  <Space size={6}>
                    <Text style={{ fontFamily: "'DM Mono', monospace" }}>
                      1 JPY = {Number(order.exchange_rate).toFixed(6)} CNY
                    </Text>
                    <RateHistoryButton />
                  </Space>
                </Row>
              )}
              {[
                { label: t('orders.itemPrice'), value: formatCNY(order.item_price_cny) },
                { label: t('orders.serviceFee'), value: formatCNY(order.service_fee_cny) },
                { label: t('orders.shippingFee'), value: formatCNY(order.shipping_fee_cny) },
                { label: t('orders.insuranceFee'), value: formatCNY(order.insurance_fee_cny) },
              ]
                .filter((row) => row.value !== '—')
                .map((row) => (
                  <Row justify="space-between" key={row.label} style={{ marginBottom: 6 }}>
                    <Text style={{ color: '#888' }}>{row.label}</Text>
                    <Text>{row.value} CNY</Text>
                  </Row>
                ))}
              <Divider style={{ margin: '10px 0' }} />
              <Row justify="space-between">
                <Text strong>{t('orders.totalAmount')}</Text>
                <Text style={{ color: GOLD, fontWeight: 700, fontSize: 18 }}>
                  {formatCNY(order.total_amount_cny)} CNY
                </Text>
              </Row>
            </div>
          </Card>
        </Col>

        {/* Right col */}
        <Col xs={24} md={10}>
          {/* Inspection */}
          {inspection && (
            <Card
              title={
                <Space>
                  <FileSearchOutlined style={{ color: GOLD }} />
                  <Text strong>{t('orders.inspectionReport')}</Text>
                </Space>
              }
              style={{ borderRadius: 8, marginBottom: 24 }}
            >
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: `${GRADE_COLORS[inspection.grade] || '#888'}20`,
                    border: `2px solid ${GRADE_COLORS[inspection.grade] || '#888'}`,
                    fontSize: 28,
                    fontWeight: 800,
                    color: GRADE_COLORS[inspection.grade] || '#888',
                    marginBottom: 8,
                  }}
                >
                  {inspection.grade || '—'}
                </div>
                <div>
                  <Text style={{ color: '#888', fontSize: 13 }}>{t('orders.overallGrade')}</Text>
                </div>
              </div>

              {inspection.scores && (
                <div style={{ marginBottom: 12 }}>
                  {Object.entries(inspection.scores).map(([key, val]) => (
                    <Row key={key} align="middle" style={{ marginBottom: 8 }}>
                      <Col span={8}>
                        <Text style={{ color: '#888', fontSize: 13 }}>{key}</Text>
                      </Col>
                      <Col span={16}>
                        <Progress
                          percent={val}
                          size="small"
                          strokeColor={GOLD}
                          format={(p) => `${p}${t('orders.scoreUnit')}`}
                        />
                      </Col>
                    </Row>
                  ))}
                </div>
              )}

              {inspection.notes && (
                <div style={{ marginBottom: 12 }}>
                  <Text style={{ color: '#888', fontSize: 13, display: 'block', marginBottom: 4 }}>
                    {t('orders.inspectionNotes')}
                  </Text>
                  <Text style={{ fontSize: 13 }}>{inspection.notes}</Text>
                </div>
              )}

              {inspectionPhotos.length > 0 && (
                <div>
                  <Text style={{ color: '#888', fontSize: 13, display: 'block', marginBottom: 8 }}>
                    {t('orders.inspectionPhotos')}
                  </Text>
                  <Image.PreviewGroup>
                    <Row gutter={[8, 8]}>
                      {inspectionPhotos.map((url, i) => (
                        <Col key={i} span={8}>
                          <Image
                            src={url}
                            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4 }}
                          />
                        </Col>
                      ))}
                    </Row>
                  </Image.PreviewGroup>
                </div>
              )}

              {inspection.inspected_at && (
                <Text style={{ color: '#aaa', fontSize: 12, display: 'block', marginTop: 8 }}>
                  {t('orders.inspectionTime')}：{dayjs(inspection.inspected_at).format('YYYY-MM-DD HH:mm')}
                </Text>
              )}
            </Card>
          )}

          {/* Shipment */}
          {shipment && (
            <Card
              title={
                <Space>
                  <CarOutlined style={{ color: GOLD }} />
                  <Text strong>{t('orders.logisticsTracking')}</Text>
                </Space>
              }
              style={{ borderRadius: 8 }}
            >
              {shipment.tracking_number && (
                <div style={{ marginBottom: 16 }}>
                  <Text style={{ color: '#888', fontSize: 13 }}>{t('orders.trackingNumber')}：</Text>
                  <Text strong style={{ fontSize: 13 }}>{shipment.tracking_number}</Text>
                </div>
              )}
              {shipment.carrier && (
                <div style={{ marginBottom: 16 }}>
                  <Text style={{ color: '#888', fontSize: 13 }}>{t('orders.carrier')}：</Text>
                  <Text style={{ fontSize: 13 }}>{shipment.carrier}</Text>
                </div>
              )}

              {shipEvents.length > 0 && (
                <Timeline
                  items={shipEvents.map((ev, i) => ({
                    color: i === 0 ? GOLD : '#d9d9d9',
                    dot: ev.icon || undefined,
                    children: (
                      <div>
                        <Text style={{ fontSize: 13 }}>{ev.label}</Text>
                        {ev.time && (
                          <Text
                            style={{ color: '#aaa', fontSize: 12, display: 'block' }}
                          >
                            {dayjs(ev.time).format('MM-DD HH:mm')}
                          </Text>
                        )}
                      </div>
                    ),
                  }))}
                />
              )}
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
